import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { EN_DICT, ES_DICT } from './KitRequestForm.locales'
import { client } from '../../providers/data/client'
import { useProviderContext } from '../../context/ProviderContext'

const initialFormState = {
  providerGroupId: '',
  providerLocationId: '',
  clinicName: '',
  providerName: '',
  dateOfVasectomy: '',
  patientFirstName: '',
  patientLastName: '',
  patientDob: '',
  shippingAddress1: '',
  shippingAddress2: '',
  shippingCity: '',
  shippingState: '',
  shippingZip: '',
  phoneNumber: '',
  email: '',
  agreedToTerms: false,
}

// Formats a US phone number as (xxx) xxx-xxxx, for both fresh typing and a stored
// +1XXXXXXXXXX value loaded from a record (all kit requests are saved in E.164, so this
// case has to be handled explicitly — not just "starts with +"). Genuine non-US E.164
// numbers (any other country code) are left untouched.
const formatPhoneInput = (value) => {
  if (!value) return ''
  const trimmed = value.trim()

  let digits
  if (trimmed.startsWith('+1')) {
    digits = trimmed.slice(2).replace(/\D/g, '').slice(0, 10)
  } else if (trimmed.startsWith('+')) {
    return value
  } else {
    digits = trimmed.replace(/\D/g, '').slice(0, 10)
  }

  if (digits.length === 0) return ''
  if (digits.length < 4) return `(${digits}`
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/**
 * @deprecated
 * This internal form component is used within the modal for launching kit requests from the portal.
 * It might be deprecated/removed in the future if we transition entirely to using the public link form
 * generated below the provider group dropdown.
 */
const KitRequestForm = ({ onCancel, onSuccess, providerAccessId, identity, providerGroupId }) => {
  const { providerInfo } = useProviderContext()
  const [lang, setLang] = useState('EN')
  const t = lang === 'EN' ? EN_DICT : ES_DICT

  const today = new Date()
  const maxYear = today.getFullYear() - 16
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  const maxDobString = `${maxYear}-${month}-${day}`

  const [formData, setFormData] = useState({
    ...initialFormState,
    providerGroupId: providerGroupId || '',
  })
  const [errors, setErrors] = useState({})
  // eslint-disable-next-line no-unused-vars
  const formRef = useRef(null)

  // Auto-scroll to first error field whenever errors are set (handles both
  // client-side validation and server-side field errors from the catch block)
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = document.querySelector('[aria-invalid="true"], .Mui-error')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [errors])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [addressConfirmationAccepted, setAddressConfirmationAccepted] = useState(false)
  const [addressSuggestion, setAddressSuggestion] = useState(null)

  const [clinics, setClinics] = useState([])
  const [providers, setProviders] = useState([])
  const [selectedProviderId, setSelectedProviderId] = useState(providerInfo?.id || '')
  const [isLoadingClinics, setIsLoadingClinics] = useState(true)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)

  // 1. Fetch Clinics (Locations for selected Group) on Mount
  useEffect(() => {
    const fetchClinics = async () => {
      if (!providerGroupId) return
      setIsLoadingClinics(true)
      try {
        const locationsResponse = await client().get(`/provider-portal/provider-locations`)

        if (locationsResponse.data?.data) {
          const locations = locationsResponse.data.data
          const groupLocations = locations.filter((loc) => loc.providerGroupId === providerGroupId)

          const combinedClinics = groupLocations.map((loc) => {
            return {
              providerLocationId: loc.id,
              providerGroupId: loc.providerGroupId,
              label: loc.locationName,
              legacyClinicName: loc.locationName,
            }
          })

          setClinics(combinedClinics)

          if (combinedClinics.length === 1) {
            const c = combinedClinics[0]
            setFormData((prev) => ({
              ...prev,
              providerLocationId: c.providerLocationId,
              providerGroupId: c.providerGroupId,
              clinicName: c.legacyClinicName,
            }))
          }
        }
      } catch (err) {
        console.error('Error fetching clinics:', err)
      } finally {
        setIsLoadingClinics(false)
      }
    }
    fetchClinics()
  }, [providerGroupId])

  // 2. Fetch Providers when Location changes
  useEffect(() => {
    const fetchProviders = async () => {
      if (!formData.providerLocationId) {
        setProviders([])
        return
      }
      setIsLoadingProviders(true)
      try {
        const response = await client().get(`/provider-portal/providers?_providerLocationId=${formData.providerLocationId}`)
        if (response.data && response.data.data) {
          const fetchedProviders = response.data.data
          setProviders(fetchedProviders)

          // If we have a providerInfo from context, try to find it in the list and set as default
          if (providerInfo?.id) {
            const current = fetchedProviders.find((p) => p.id === providerInfo.id)
            if (current) {
              setFormData((prev) => ({
                ...prev,
                providerName: current.displayName,
              }))
              setSelectedProviderId(current.id)
            }
          } else if (fetchedProviders.length === 1) {
            // Auto-select if only one provider
            const p = fetchedProviders[0]
            setFormData((prev) => ({
              ...prev,
              providerName: p.displayName,
            }))
            setSelectedProviderId(p.id)
          }
        }
      } catch (err) {
        console.error('Error fetching providers:', err)
      } finally {
        setIsLoadingProviders(false)
      }
    }
    fetchProviders()
  }, [formData.providerLocationId, providerInfo])

  const calculateAge = (dobString) => {
    if (!dobString) return 0
    const dob = new Date(dobString)
    const today = new Date()
    let age = today.getFullYear() - dob.getFullYear()
    const monthDiff = today.getMonth() - dob.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--
    }
    return age
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    // Restrict invalid inputs for first and last name (Alphabets only)
    if ((name === 'patientFirstName' || name === 'patientLastName') && value) {
      const alphaValue = value.replace(/[^A-Za-z\s]/g, '')
      if (alphaValue !== value) return
    }

    // Restrict invalid inputs for phone number while typing, allow '+' for E.164 entry
    // and '-' for the auto-inserted (xxx) xxx-xxxx formatting below.
    let nextValue = value
    if (name === 'phoneNumber' && value) {
      const numericValue = value.replace(/[^\d\s()+-]/g, '')
      if (numericValue !== value) return // Ignore invalid char
      nextValue = formatPhoneInput(value)
    }

    let updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : nextValue,
    }

    // Update related fields if location changes
    if (name === 'providerLocationId') {
      const selectedClinic = clinics.find((c) => c.providerLocationId === value)
      if (selectedClinic) {
        updatedFormData.providerGroupId = selectedClinic.providerGroupId
        updatedFormData.clinicName = selectedClinic.legacyClinicName
        updatedFormData.providerName = ''
        setSelectedProviderId('')
      } else {
        updatedFormData.providerGroupId = ''
        updatedFormData.clinicName = ''
        updatedFormData.providerName = ''
        setSelectedProviderId('')
      }
    }

    // Store selected provider ID if providerName is changed
    if (name === 'providerName') {
      const selectedProvider = providers.find((p) => p.displayName === value)
      if (selectedProvider) {
        setSelectedProviderId(selectedProvider.id)
      }
    }

    if (['shippingAddress1', 'shippingAddress2', 'shippingCity', 'shippingState', 'shippingZip'].includes(name)) {
      setAddressConfirmationAccepted(false)
    }

    setFormData(updatedFormData)
    // Clear error(s) on change
    if (name === 'providerLocationId') {
      // This field controls both providerLocationId and clinicName errors
      setErrors((prev) => ({ ...prev, providerLocationId: null, clinicName: null }))
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  const isFutureDate = (dateString) => {
    if (!dateString) return false
    const selected = new Date(dateString)
    const today = new Date()
    today.setHours(23, 59, 59, 999) // Ignore time
    return selected > today
  }

  const validate = () => {
    const newErrors = {}

    // Required fields
    const requiredFields = [
      'providerLocationId',
      'clinicName',
      'providerName',
      'dateOfVasectomy',
      'patientFirstName',
      'patientLastName',
      'patientDob',
      'shippingAddress1',
      'shippingCity',
      'shippingState',
      'shippingZip',
      'email',
      'phoneNumber',
    ]

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = t.requiredError
      }
    })

    // Name validations (Double check in case of copy-paste)
    const nameRegex = /^[A-Za-z\s]+$/
    if (formData.patientFirstName && !nameRegex.test(formData.patientFirstName)) {
      newErrors.patientFirstName = 'Only alphabets are allowed'
    }
    if (formData.patientLastName && !nameRegex.test(formData.patientLastName)) {
      newErrors.patientLastName = 'Only alphabets are allowed'
    }

    // Date validations
    if (formData.dateOfVasectomy) {
      const dov = new Date(formData.dateOfVasectomy)
      const minDov = new Date()
      minDov.setFullYear(minDov.getFullYear() - 10)
      const maxDov = new Date()
      maxDov.setFullYear(maxDov.getFullYear() + 1)
      if (dov < minDov || dov > maxDov) {
        newErrors.dateOfVasectomy = t.dovRangeError
      }
    }
    // Note: Future dates are now allowed for dateOfVasectomy as requested
    if (formData.patientDob) {
      if (isFutureDate(formData.patientDob)) {
        newErrors.patientDob = t.futureDateError
      } else if (calculateAge(formData.patientDob) < 16) {
        newErrors.patientDob = t.ageError
      }
    }

    // Zip Code (Flexible for US, Canada, etc.)
    if (formData.shippingZip) {
      const zipRegex = /^[A-Za-z0-9\s-]{3,10}$/
      if (!zipRegex.test(formData.shippingZip)) {
        newErrors.shippingZip = t.zipError
      }
    }

    // Contact format validation (Robust Email Regex)
    if (formData.email) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t.emailError
      }
    }

    if (formData.phoneNumber) {
      // Robust phone validation: strip everything but digits
      const phoneClean = formData.phoneNumber.replace(/\D/g, '')
      const isE164 = formData.phoneNumber.trim().startsWith('+')

      // If it starts with +, it should be at least 7 digits (international min)
      // If it doesn't, it must be exactly 10 digits (US)
      if (isE164) {
        if (phoneClean.length < 7 || phoneClean.length > 15) {
          newErrors.phoneNumber = t.phoneError
        }
      } else if (phoneClean.length !== 10) {
        newErrors.phoneNumber = t.phoneError
      }
    }

    // Agreement
    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = t.agreeError
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Standardize phone number to E.164 format for the backend
      let formattedPhone = formData.phoneNumber.trim().replace(/\s+/g, '')
      if (!formattedPhone.startsWith('+')) {
        const cleanDigits = formattedPhone.replace(/\D/g, '')
        // If it's 10 digits, assume US and add +1
        // If it's 11 digits and starts with 1, just add +
        if (cleanDigits.length === 10) {
          formattedPhone = `+1${cleanDigits}`
        } else if (cleanDigits.length === 11 && cleanDigits.startsWith('1')) {
          formattedPhone = `+${cleanDigits}`
        } else {
          // Fallback for other lengths entered without +
          formattedPhone = `+${cleanDigits}`
        }
      }

      const payload = {
        ...formData,
        phoneNumber: formattedPhone,
        dateOfVasectomy: formData.dateOfVasectomy ? new Date(formData.dateOfVasectomy).toISOString() : formData.dateOfVasectomy,
        patientDob: formData.patientDob ? new Date(formData.patientDob).toISOString() : formData.patientDob,
        providerId: selectedProviderId || providerInfo?.id, // Using the selected provider ID
        providerAccessId: providerAccessId,
        language: lang,
        addressConfirmationAccepted,
        notificationMethod: 'Both',
      }

      console.log('Submitting Kit Request:', payload)

      const response = await client().post('/provider-portal/kit-requests', payload)

      if (response.status === 201) {
        setIsSuccess(true)
        if (onSuccess) onSuccess()
      } else {
        throw new Error(response.data?.message || 'Failed to submit kit request')
      }
    } catch (err) {
      console.error('Error submitting form:', err)
      const responseData = err.response?.data

      if (responseData?.requiresAddressConfirmation && responseData?.suggestedAddress) {
        setAddressSuggestion({
          message: responseData?.message || t.addressSuggestionMessage,
          enteredAddress: responseData?.enteredAddress || {},
          suggestedAddress: responseData?.suggestedAddress || {},
        })
        return
      }

      // If the server returned structured per-field address errors, surface them
      // on the individual form fields using the same errors state as client-side
      // validation so each shipping field shows its own inline helper text.
      if (responseData?.fieldErrors && Object.keys(responseData.fieldErrors).length > 0) {
        // Translate Google address validation errors into the active language.
        // t.addressErrors is a map of English message → translated message;
        // for EN the map is empty so messages pass through unchanged.
        const translatedFieldErrors = Object.fromEntries(
          Object.entries(responseData.fieldErrors).map(([field, msg]) => [field, t.addressErrors[msg] || msg])
        )
        setErrors((prev) => ({ ...prev, ...translatedFieldErrors }))
      }

      setSubmitError(responseData?.message || err.message || 'An error occurred during submission')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleNewForm = () => {
    setFormData({
      ...initialFormState,
      providerGroupId: formData.providerGroupId,
      providerLocationId: formData.providerLocationId,
      clinicName: formData.clinicName,
      providerName: formData.providerName,
    })
    setErrors({})
    setIsSuccess(false)
    setSubmitError(null)
    setAddressConfirmationAccepted(false)
    setAddressSuggestion(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const applySuggestedAddress = () => {
    if (!addressSuggestion?.suggestedAddress) return

    setFormData((prev) => ({
      ...prev,
      ...addressSuggestion.suggestedAddress,
    }))
    setAddressConfirmationAccepted(true)
    setAddressSuggestion(null)
    setSubmitError(t.addressSuggestionApplied)
  }

  const keepEnteredAddress = () => {
    setAddressConfirmationAccepted(true)
    setAddressSuggestion(null)
    setSubmitError(t.addressEnteredConfirmed)
  }

  const closeAddressSuggestion = () => {
    setAddressSuggestion(null)
  }

  if (isSuccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 8 }}>
        <Typography variant="h4" color="primary" gutterBottom>
          {t.successTitle}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          {t.successMessage}
        </Typography>
        <Button variant="contained" onClick={handleNewForm}>
          {t.newForm}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto', height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant={lang === 'EN' ? 'contained' : 'outlined'} onClick={() => setLang('EN')} sx={{ mr: 1 }} size="small">
          English
        </Button>
        <Button variant={lang === 'ES' ? 'contained' : 'outlined'} onClick={() => setLang('ES')} size="small">
          Español
        </Button>
      </Box>

      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
        {t.title}
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Clinic & Doctor */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.providerLocationId || !!errors.clinicName} disabled={isLoadingClinics}>
              <InputLabel id="clinic-name-label">{isLoadingClinics ? 'Loading...' : `${t.clinicName} *`}</InputLabel>
              <Select
                labelId="clinic-name-label"
                name="providerLocationId"
                value={formData.providerLocationId}
                label={isLoadingClinics ? 'Loading...' : `${t.clinicName} *`}
                onChange={handleChange}
                IconComponent={isLoadingClinics ? () => <CircularProgress size={20} sx={{ mr: 1 }} /> : undefined}
              >
                {clinics.map((clinic) => (
                  <MenuItem key={clinic.providerLocationId} value={clinic.providerLocationId}>
                    {clinic.label}
                  </MenuItem>
                ))}
              </Select>
              {(errors.providerLocationId || errors.clinicName) && (
                <FormHelperText>{errors.providerLocationId || errors.clinicName}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.providerName} disabled={!formData.providerLocationId || isLoadingProviders}>
              <InputLabel id="provider-name-label">{isLoadingProviders ? 'Loading...' : `${t.providerName} *`}</InputLabel>
              <Select
                labelId="provider-name-label"
                name="providerName"
                value={formData.providerName}
                label={isLoadingProviders ? 'Loading...' : `${t.providerName} *`}
                onChange={handleChange}
                IconComponent={isLoadingProviders ? () => <CircularProgress size={20} sx={{ mr: 1 }} /> : undefined}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.displayName}>
                    {provider.displayName}
                  </MenuItem>
                ))}
              </Select>
              {errors.providerName && <FormHelperText>{errors.providerName}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Date of Vasectomy */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={`${t.dateOfVasectomy} *`}
              name="dateOfVasectomy"
              InputLabelProps={{ shrink: true }}
              value={formData.dateOfVasectomy}
              onChange={handleChange}
              error={!!errors.dateOfVasectomy}
              helperText={errors.dateOfVasectomy}
            />
          </Grid>
          <Grid item xs={12} sm={6} />

          {/* Patient Info */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t.patientFirstName} *`}
              name="patientFirstName"
              value={formData.patientFirstName}
              onChange={handleChange}
              error={!!errors.patientFirstName}
              helperText={errors.patientFirstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t.patientLastName} *`}
              name="patientLastName"
              value={formData.patientLastName}
              onChange={handleChange}
              error={!!errors.patientLastName}
              helperText={errors.patientLastName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label={`${t.patientDob} *`}
              name="patientDob"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: maxDobString }}
              value={formData.patientDob}
              onChange={handleChange}
              error={!!errors.patientDob}
              helperText={errors.patientDob}
            />
          </Grid>
          <Grid item xs={12} sm={6} />

          {/* Shipping Address */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t.patientShippingAddress} *
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={`${t.streetAddress} *`}
              name="shippingAddress1"
              value={formData.shippingAddress1}
              onChange={handleChange}
              error={!!errors.shippingAddress1}
              helperText={errors.shippingAddress1}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t.streetAddress2}
              name="shippingAddress2"
              value={formData.shippingAddress2}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={`${t.city} *`}
              name="shippingCity"
              value={formData.shippingCity}
              onChange={handleChange}
              error={!!errors.shippingCity}
              helperText={errors.shippingCity}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={`${t.state} *`}
              name="shippingState"
              value={formData.shippingState}
              onChange={handleChange}
              error={!!errors.shippingState}
              helperText={errors.shippingState}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={`${t.zip} *`}
              name="shippingZip"
              value={formData.shippingZip}
              onChange={handleChange}
              error={!!errors.shippingZip}
              helperText={errors.shippingZip}
            />
          </Grid>

          {/* Contact Details */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t.phoneNumber} *`}
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              helperText={errors.phoneNumber || t.phoneDesc}
              error={!!errors.phoneNumber}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={`${t.email} *`}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              helperText={errors.email || t.emailDesc}
              error={!!errors.email}
            />
          </Grid>

          {/* Patient Instructions */}
          <Grid item xs={12}>
            <Typography variant="h6" color="error" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
              {t.patientInstructionsTitle}
            </Typography>
            <Box sx={{ color: 'error.main', fontSize: '15px', '& p': { mb: 2 } }}>
              <p>{t.instruction1}</p>
              <p>{t.instruction2}</p>
              <p dangerouslySetInnerHTML={{ __html: t.instruction3 }} />
              <Box sx={{ mb: 2 }}>
                <p style={{ marginBottom: '8px' }}>{t.instruction4}</p>
                <Box sx={{ pl: 3 }}>
                  <div>{t.instruction4a}</div>
                  <div>{t.instruction4b}</div>
                  <div>{t.instruction4c}</div>
                </Box>
              </Box>
              <p>{t.instruction5}</p>
              <p>{t.instruction6}</p>
              <p style={{ marginTop: '24px' }} dangerouslySetInnerHTML={{ __html: t.contactHelp }} />
            </Box>
          </Grid>

          {/* Terms Agreement */}
          <Grid item xs={12}>
            <FormControl error={!!errors.agreedToTerms}>
              <FormControlLabel
                control={<Checkbox name="agreedToTerms" checked={formData.agreedToTerms} onChange={handleChange} />}
                label={
                  <Box component="span" sx={{ fontSize: '16px' }}>
                    {t.agreeLabelText1}
                    <a
                      href="https://cdn.shopify.com/s/files/1/2695/5150/files/MFH-TermsOfService-20201204.pdf?v=1608269512"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                    >
                      {t.agreeLabelLink}
                    </a>
                    {t.agreeLabelText2} <span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                  </Box>
                }
                sx={{ alignItems: 'center', mt: 1 }}
              />
              {errors.agreedToTerms && <FormHelperText sx={{ ml: 4 }}>{errors.agreedToTerms}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Submit/Cancel Buttons */}
          <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 2, mb: 4 }}>
            <Button variant="outlined" color="secondary" size="large" onClick={onCancel} disabled={isSubmitting}>
              {t.cancel}
            </Button>
            <Button type="submit" variant="contained" color="primary" size="large" disabled={isSubmitting} sx={{ px: 4 }}>
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t.submit}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Dialog open={Boolean(addressSuggestion)} onClose={closeAddressSuggestion} maxWidth="sm" fullWidth>
        <DialogTitle>{t.addressSuggestionTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {addressSuggestion?.message || t.addressSuggestionMessage}
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {t.addressYouEntered}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
            {[
              addressSuggestion?.enteredAddress?.shippingAddress1,
              addressSuggestion?.enteredAddress?.shippingAddress2,
              `${addressSuggestion?.enteredAddress?.shippingCity || ''}, ${addressSuggestion?.enteredAddress?.shippingState || ''} ${addressSuggestion?.enteredAddress?.shippingZip || ''}`.trim(),
            ].filter(Boolean).join('\n')}
          </Typography>

          <Typography variant="subtitle2">{t.addressSuggested}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {[
              addressSuggestion?.suggestedAddress?.shippingAddress1,
              addressSuggestion?.suggestedAddress?.shippingAddress2,
              `${addressSuggestion?.suggestedAddress?.shippingCity || ''}, ${addressSuggestion?.suggestedAddress?.shippingState || ''} ${addressSuggestion?.suggestedAddress?.shippingZip || ''}`.trim(),
            ].filter(Boolean).join('\n')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepEnteredAddress}>{t.addressKeepEntered}</Button>
          <Button variant="contained" onClick={applySuggestedAddress}>{t.addressUseSuggested}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default KitRequestForm
